import { Configuration } from './../../models/configuration.model';
import { User } from './../../models/identity/user.model';
import { of as observableOf, Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../services/language.service';
import { Language } from '../../models/language.model';
import { ConfigService } from '../../services/config.service';
import * as moment from 'moment';
import { NamedEntity } from '../../models/named-entity.model';
import { UserService } from '../../services/user.service';
import { Router } from '@angular/router';
import {
  trigger,
  state,
  style,
  transition,
  animate,
  keyframes,
} from '@angular/animations';
import { EventNotificationService } from '../../services/event-notification.service';
import { NgxScrollEvent } from 'ngx-scroll-event/dist/ngx-scroll-event';
import { LocalizedName } from '../../models/localized-name.model';
import { NotificationMessage } from '../../models/event-notifications/notification-message.model';
import { EventNotificationTypes } from '../../models/event-notifications/event-notification-types.enum';
import { delay, map } from 'rxjs/operators';
import { ProcessParticipationApprovalRequirementType } from '../../models/process-models';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('flyInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      state('out', style({ transform: 'translateX(0)' })),
      transition('void => *', [
        animate(
          1000,
          keyframes([
            style({ opacity: 0, transform: 'translateX(-100%)', offset: 0 }),
            style({ opacity: 1, transform: 'translateX(15px)', offset: 0.3 }),
            style({ opacity: 1, transform: 'translateX(0)', offset: 1.0 }),
          ])
        ),
      ]),
      transition('out => void', [
        animate(
          300,
          keyframes([
            style({ opacity: 1, transform: 'translateX(0)', offset: 0 }),
            style({ opacity: 1, transform: 'translateX(-15px)', offset: 0.7 }),
            style({ opacity: 0, transform: 'translateX(100%)', offset: 1.0 }),
          ])
        ),
      ]),
    ]),
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input()
  isAdminRoute: boolean;
  isLeaverMode: boolean = false;
  notifications: Array<NotificationMessage> = [];
  isAdmin = false;
  isNotificationBarOpened = false;
  notificationTypes: typeof EventNotificationTypes = EventNotificationTypes;
  avaliableLanguages: Array<Language>;
  employeeOptions: Array<NamedEntity> = [];
  configuration: Configuration;


  @ViewChild('dropdown') dropdown: BsDropdownDirective;

  private currentLanguageNameKey: string;
  private cookieExpiresDate: Date;
  private readonly subscription: Subscription = new Subscription();
  private selectedEmployeeId: number;
  private hasUserUnviewedNotifications = false;
  animationState = 'in';

  constructor(
    private readonly languageService: LanguageService,
    private readonly configService: ConfigService,
    private readonly cookieService: CookieService,
    private readonly userService: UserService,
    private readonly translateService: TranslateService,
    private readonly eventNotificationService: EventNotificationService,
    private readonly loginService: LoginService,
    private readonly router: Router
  ) {
    if(!loginService.getUser) {
      return;
    }
    this.setCookieKey(configService.configData);
  }

  async ngOnInit() {
    this.configuration = this.configService.configData as Configuration;
    this.subscription.add(this.loginService.employeeLoggedIn.subscribe(async x => {
        await this.resetComponent();
    }))
    if(!this.loginService.getUser) {
      return;
    }
    await this.resetComponent();
  }

  async resetComponent() {
    this.cookieExpiresDate = this.getNextMonthDate();
    this.avaliableLanguages = this.configService.getLanguages?? await this.configService.getAllLanguagesAsync();
    console.log(this.avaliableLanguages)
    const currentLocale = this.cookieService.get(this.currentLanguageNameKey);
    await this.resetNotifications();
    if (currentLocale) {
      this.setMomentLocale(currentLocale);
      return;
    }
    const languageCode = await this.languageService
    .getDefaultLanguage().pipe(map(x => x.code)).toPromise();
    this.setLanguage(languageCode)
}

  async resetNotifications() {
    let notificatinResult = await this.eventNotificationService
      .getNotifications(0)
      .toPromise();
    this.hasUserUnviewedNotifications = notificatinResult.hasUnviewed;
    let actualNotifications = this.getParsedNotifications(
      notificatinResult.notifications
    );
    let newNotifications = actualNotifications.filter(
      (n) => this.notifications.findIndex((nt) => nt.id == n.id) == -1
    );
    let obsoleteNotifications = this.notifications.filter(
      (n) => actualNotifications.findIndex((nt) => nt.id == n.id) == -1
    );
    newNotifications.forEach((n) => this.notifications.push(n));
    obsoleteNotifications.forEach((n) => {
      const index = this.notifications.indexOf(n, 0);
      if (index > -1) {
        this.notifications.splice(index, 1);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public get isOriginalIdentity() {
    return this.loginService.getUser.isOriginalIdentity;
  }

  public get hasUnviwedNotifications(): boolean {
    return (
      this.hasUserUnviewedNotifications &&
      this.notifications.some((ms) => !ms.viewed)
    );
  }

  public handleScroll(event: NgxScrollEvent) {
    if (
      this.isNotificationBarOpened &&
      (event.isReachingBottom || event.isReachingTop)
    ) {
      this.loadNotifications();
    }
  }

  public loadNotifications() {
    this.eventNotificationService
      .getNotifications(this.notifications.length)
      .subscribe((data) => {
        this.hasUserUnviewedNotifications = data.hasUnviewed;
        if (data.notifications.length === 0) {
          return;
        }
        this.notifications = this.notifications.concat(
          this.getParsedNotifications(data.notifications)
        );
      });
  }

  public getLocateName(localizedNames: Array<LocalizedName>) {
    return localizedNames.find(
      (ln) => ln.locale === this.cookieService.get(this.currentLanguageNameKey)
    ).name as string;
  }

  public getIsChosenLanguage(currentLanguage: string): boolean {
    return (
      this.cookieService.get(this.currentLanguageNameKey) === currentLanguage
    );
  }

  public setLanguage(targetlanguageName: string): void {
    const currentLangName = this.cookieService.get(this.currentLanguageNameKey);
    this.cookieService.set(
      this.currentLanguageNameKey,
      targetlanguageName,
      this.cookieExpiresDate,
      '/'
    );
    this.translateService.use(targetlanguageName.toLowerCase());
    this.setMomentLocale(targetlanguageName);
    if (currentLangName !== targetlanguageName) {
      this.languageService.emitChangeLanguage(targetlanguageName);
    }
  }

  public clearEmployeeSelection() {
    this.clearEmployeeOptions();
    this.selectedEmployeeId = 0;
  }

  public clearEmployeeOptions() {
    this.employeeOptions = [];
  }

  public selectedEmployeeOption(employeeId: number) {
    this.selectedEmployeeId = this.employeeOptions.find(
      (e) => e.id === employeeId
    ).id;
  }

  public typedEmployeeOptions(employeePattern: string) {
    if (!this.getIsStartSearch(employeePattern)) {
      this.clearEmployeeOptions();
      return;
    }
    this.userService
      .getBySearchPattern(employeePattern, this.isLeaverMode)
      .subscribe((r) => {
        this.employeeOptions = r.map((p) => ({
          id: p.id,
          name: this.isLeaverMode
            ? p.gpid
            : `${p.name}${p.gpid ? ` - ${p.gpid}` : ''}`,
        }));
      });
  }

  public cancelImpersonateSelection() {
    this.selectedEmployeeId = 0;
    this.closeNotificationPanel();
  }

  public impersonateEmployee() {
    this.cookieService.set(
      this.configService.configData.cookieImpersonationKey,
      this.selectedEmployeeId.toString(),
      this.cookieExpiresDate,
      '/'
    );
    this.refreshPage();
  }

  public cancelImpersonation() {
    this.cookieService.delete(
      this.configService.configData.cookieImpersonationKey,
      '/'
    );
    this.refreshPage();
  }

  public deleteNotification(message: NamedEntity, event: Event) {
    this.animationState = 'out';
    event.stopPropagation();
    this.eventNotificationService
      .deleteEventNotification(message.id)
      .subscribe((response) => {
        const index = this.notifications.indexOf(message);
        this.notifications.splice(index, 1);
        this.animationState = 'in';
      });
  }

  public deleteNotificationAll() {
    this.animationState = 'out';
    this.eventNotificationService
      .deleteAllEventNotifications()
      .subscribe((response) => {
        this.notifications = [];
        this.closeNotificationPanel();
        this.animationState = 'in';
      });
  }

  public showNotificationType(type: number) {
    if (
      type === this.notificationTypes.commonCommentNotificationType ||
      type ===
        this.notificationTypes.commentPerformanceManagerNotificationType ||
      type === this.notificationTypes.anonymCommonCommentNotificationType ||
      type ===
        this.notificationTypes.anonymCommentPerformanceManagerNotificationType
    ) {
      return 'ti-comment-alt';
    } else if (
      type === this.notificationTypes.creationRespondentListNotificationType
    ) {
      return 'ti-agenda';
    } else if (
      type ===
      this.notificationTypes.processParticipationAssingnedNotificationType
    ) {
      return 'ti-comment-alt';
    } else if (
      type ===
        this.notificationTypes
          .confirmationRespondentsBusinessUnitAdministratorNotificationType ||
      type ===
        this.notificationTypes
          .confirmationRespondentsPerformanceManagerNotificationType
    ) {
      return 'ti-agenda';
    } else {
      return '';
    }
  }

  public formatDate(source: Date) {
    const appLanguage = this.cookieService.get(
      this.configService.configData.cookieLanguageNameKey
    );
    const offset = moment().utcOffset();
    return moment
      .utc(source)
      .utcOffset(offset)
      .locale(appLanguage)
      .format('H:mm | D MMMM');
  }

  public goToNotificationSourcePage(
    notification: NotificationMessage,
    notificationType: number
  ) {
    this.eventNotificationService
      .readEventNotification(notification.id)
      .subscribe((data) => {
        const targetId = this.notifications.findIndex(
          (x) => x.id === notification.id
        );
        this.notifications[targetId].viewed = true;
        this.closeNotificationBar();
        this.chooseSourcePageFromNotification(notification, notificationType);
      });
  }

  public async toggleNotificationBar(event: Event) {
    if (!this.isNotificationBarOpened) {
      await this.resetNotifications();
    }
    event.stopPropagation();
    this.isNotificationBarOpened = !this.isNotificationBarOpened;
    this.dropdown.toggle(true);
  }

  onClickedOutside(e: Event) {
    if (this.isNotificationBarOpened) {
      this.closeNotificationBar();
    }
  }

  get user(): User {
    return this.loginService.getUser;
  }


  private closeNotificationBar() {
    this.isNotificationBarOpened = false;
    this.dropdown.hide();
  }

  private chooseSourcePageFromNotification(
    notification: NotificationMessage,
    notificationType: number
  ) {
    switch (notificationType) {
      case this.notificationTypes
        .anonymCommentPerformanceManagerNotificationType:
        this.router.navigate([
          'user/my-team/feedbacks',
          {
            senderId: notification.body.senderId,
            teammateEmployeeId: notification.body.receiverId,
            isanonym: 'true',
          },
        ]);
        break;
      case this.notificationTypes.anonymCommonCommentNotificationType:
        this.router.navigate([`user/feedback-chat`, { isanonym: 'true' }]);
        break;
      case this.notificationTypes.commentPerformanceManagerNotificationType:
        this.router.navigate([
          'user/my-team/feedbacks',
          {
            senderId: notification.body.senderId,
            teammateEmployeeId: notification.body.receiverId,
          },
        ]);
        break;
      case this.notificationTypes.commonCommentNotificationType:
        this.router.navigate([
          'user/feedback-chat',
          { senderId: notification.body.senderId, isanonym: 'false' },
        ]);
        break;
      case this.notificationTypes
        .confirmationRespondentsBusinessUnitAdministratorNotificationType:
        this.router.navigate([
          `user/my-bu/confirmations-bu-detail/${notification.body.processDurationAppraiseeId}`,
          {
            requirementType:
              ProcessParticipationApprovalRequirementType.ByDepartmentAdministrator,
          },
        ]);
        break;
      case this.notificationTypes
        .confirmationRespondentsPerformanceManagerNotificationType:
        this.router.navigate([
          `user/my-team/confirmations-bu-detail/${notification.body.processDurationAppraiseeId}`,
          {
            requirementType:
              ProcessParticipationApprovalRequirementType.ByPerformanceManager,
          },
        ]);
        break;
      case this.notificationTypes.creationRespondentListNotificationType:
        this.router.navigate([
          `user/my-page/confirmations-detail/${notification.body.processDurationAppraiseeId}`,
          {
            requirementType:
              ProcessParticipationApprovalRequirementType.ByAppraisee,
          },
        ]);
        break;
      case this.notificationTypes.processParticipationAssingnedNotificationType:
        this.router.navigate([
          'user/survey-list/survey',
          { id: notification.body.processParticipationId },
        ]);
        break;
    }
  }

  private setMomentLocale(locale: string) {
    moment.locale(locale.toLowerCase());
  }

  private closeNotificationPanel() {
    const logOnBtn: HTMLElement = document.getElementById(
      'button-basic'
    ) as HTMLElement;
    observableOf({})
      .pipe(
        delay(
          this.configService.configData.notificationWindowCloseDelayMilliseconds
        )
      )
      .subscribe((x) => logOnBtn.click());
  }

  private refreshPage(): void {
    this.router.navigate(['']);
    window.location.reload();
  }

  private setCookieKey(source) {
    this.currentLanguageNameKey = source.cookieLanguageNameKey as string;
  }

  private getNextMonthDate() {
    return moment().add(1, 'month').toDate();
  }

  private getIsStartSearch(pattern: string): boolean {
    return (
      pattern.length >= this.configService.configData.minimumSearchStringLength
    );
  }

  private getParsedNotifications(notifications: Array<NotificationMessage>) {
    notifications.forEach((nt) => this.parseNotificationBody(nt));
    return notifications;
  }

  private parseNotificationBody(notification: NotificationMessage) {
    notification.body = JSON.parse(notification.body);
  }
}
